import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useIsAuthenticated } from '@azure/msal-react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  notification,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Input,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getDayJsDate } from '../../utils/datetime';
import CustomInput from '../CustomComponents/CustomInput';
import {
  energyActivityOptions,
  formatOptions,
  GET_PROCEEDING_DETAILS,
  MotionDate,
  UPDATE_PROCEEDING_MUTATION,
} from './constants';

const ProceedingDetails: React.FC = () => {
  const { proceedingId } = useParams<{ proceedingId: string }>();
  const isAuthenticated = useIsAuthenticated();
  const today = new Date();
  const navigate = useNavigate();
  const [isJoint, setIsJoint] = useState('no');
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [form] = Form.useForm();
  const [selectedEnergyActivities, setSelectedEnergyActivities] = useState<string[]>([]);
  const [showOtherCommentBox, setShowOtherCommentBox] = useState(false);
  const [technicalMeetingDates, setTechnicalMeetingDates] = useState<string[]>([]);
  const [siteVisitDates, setSiteVisitDates] = useState<string[]>([]);
  const [motionDates, setMotionDates] = useState<MotionDate[]>([]);
  const [hearingDates, setHearingDates] = useState<{ startDate: string; endDate: string }[]>([]);
  const { TextArea } = Input;

  const [getProceeding, { data: proceedingDetail, loading, error }] =
    useLazyQuery(GET_PROCEEDING_DETAILS);

  const disabledInputStyle = {
    backgroundColor: '#f5f5f5',
    color: '#595959',
    opacity: '1',
    border: '1px solid #d9d9d9',
  };

  useEffect(() => {
    if (proceedingId) {
      getProceeding({ variables: { input: proceedingId } });
    }
  }, [proceedingId]);

  useEffect(() => {
    if (error) {
      notification.error({
        message: 'Error fetching proceeding details',
        description: error.message || 'An unknown error occurred.',
      });
    }
  }, [error]);

  useEffect(() => {
    if (proceedingDetail?.proceeding) {
      const formValues = {
        ...proceedingDetail.proceeding,
        referralDate: getDayJsDate(proceedingDetail.proceeding.referralDate),
        chcReceivedDate: getDayJsDate(proceedingDetail.proceeding.chcReceivedDate),
        participantRequestDeadlineDate: getDayJsDate(
          proceedingDetail.proceeding.participantRequestDeadlineDate
        ),
        hearingIssuesSetDate: getDayJsDate(proceedingDetail.proceeding.hearingIssuesSetDate),
        informationSessionDate: getDayJsDate(proceedingDetail.proceeding.informationSessionDate),
        energyActivityTypes: proceedingDetail.proceeding.energyActivityTypes || [],
        otherComment: proceedingDetail.proceeding.otherComment || '',
      };

      setTechnicalMeetingDates(proceedingDetail.proceeding.technicalMeetingDates || []);
      setSiteVisitDates(proceedingDetail.proceeding.siteVisitDates || []);
      setMotionDates(
        proceedingDetail.proceeding.motionDates
          ? proceedingDetail.proceeding.motionDates.map(
              (motion: { startDate: string; endDate: string }) => ({
                startDate: getDayJsDate(motion.startDate),
                endDate: getDayJsDate(motion.endDate),
              })
            )
          : []
      );
      setHearingDates(proceedingDetail.proceeding.hearingDates || []);
      form.setFieldsValue(formValues);
      setIsJoint(proceedingDetail.proceeding.isJoint);
      setSelectedEnergyActivities(proceedingDetail.proceeding.energyActivityTypes || []);
      setShowOtherCommentBox(proceedingDetail.proceeding.energyActivityTypes?.includes('Other'));
      setIsFormDirty(false);
    }
  }, [proceedingDetail]);

  const [updateProceeding, { loading: updating, error: updateError }] = useMutation(
    UPDATE_PROCEEDING_MUTATION,
    { onError: () => {} }
  );

  const onFinish = async (values: any) => {
    const formatDate = (date: any) =>
      date && dayjs(date).isValid() ? dayjs(date).format('YYYY-MM-DD') : '';

    const hearingDatesFormatted = hearingDates.map((date) => ({
      startDate: formatDate(date.startDate),
      endDate: formatDate(date.endDate),
    }));

    const technicalMeetingDatesFormatted = technicalMeetingDates
      .map((date) => formatDate(date))
      .filter((date) => date);

    const siteVisitDatesFormatted = siteVisitDates
      .map((date) => formatDate(date))
      .filter((date) => date);

    const motionDatesFormatted = motionDates.map((motion) => ({
      startDate: formatDate(motion.startDate),
      endDate: formatDate(motion.endDate),
    }));

    const emptyMotionDate = motionDatesFormatted.some((motion) => !motion.startDate);
    const invalidMotionDate = motionDatesFormatted.some(
      (motion) => motion.endDate && !motion.startDate
    );

    if (emptyMotionDate) {
      notification.error({
        message: 'Validation Error',
        description: 'Hearing on a Motion Start Date cannot be empty.',
      });
      return;
    }

    if (invalidMotionDate) {
      notification.error({
        message: 'Validation Error',
        description: 'Motion End Date cannot be provided without a Motion Start Date.',
      });
      return;
    }

    const emptyHearingDate = hearingDatesFormatted.some((date) => !date.startDate);
    const invalidHearingDate = hearingDatesFormatted.some(
      (date) => date.endDate && !date.startDate
    );

    if (emptyHearingDate) {
      notification.error({
        message: 'Validation Error',
        description: 'Hearing Start Date cannot be empty.',
      });
      return;
    }
    if (invalidHearingDate) {
      notification.error({
        message: 'Validation Error',
        description: 'Hearing End Date cannot be provided without a Hearing Start Date.',
      });
      return;
    }

    const invalidTechnicalMeetingDates =
      technicalMeetingDates.length > 0 && technicalMeetingDates.some((date) => !formatDate(date));
    const invalidSiteVisitDates =
      siteVisitDates.length > 0 && siteVisitDates.some((date) => !formatDate(date));

    if (invalidTechnicalMeetingDates) {
      notification.error({
        message: 'Validation Error',
        description: 'Technical Meeting Dates cannot contain empty or null values.',
      });
      return;
    }

    if (invalidSiteVisitDates) {
      notification.error({
        message: 'Validation Error',
        description: 'Site Visit Dates cannot contain empty or null values.',
      });
      return;
    }

    try {
      const updated = await updateProceeding({
        variables: {
          input: {
            id: proceedingDetail?.proceeding.id,
            chcReceivedDate: formatDate(values.chcReceivedDate),
            referralDate: formatDate(values.referralDate),
            proceedingId: proceedingId,
            isJoint: isJoint,
            location: values.location,
            format: values.format,
            description: values.description,
            participantRequestDeadlineDate: formatDate(values.participantRequestDeadlineDate),
            hearingIssuesSetDate: formatDate(values.hearingIssuesSetDate),
            issuesComment: values.issuesComment,
            reportUpdate: values.reportUpdate,
            informationSessionDate: formatDate(values.informationSessionDate),
            hearingDates: hearingDatesFormatted,
            technicalMeetingDates: technicalMeetingDatesFormatted,
            siteVisitDates: siteVisitDatesFormatted,
            motionDates: motionDatesFormatted,
            energyActivityTypes: selectedEnergyActivities,
            otherComment: values.otherComment,
          },
        },
      });

      if (updated?.data?.updateProceeding) {
        notification.success({
          message: 'Proceeding is updated',
        });
        setIsFormDirty(false);
        navigate(`/hearing/proceeding/${proceedingId}`);
      }
    } catch (error) {
      console.error('Error updating proceeding:', error);
      notification.error({
        message: 'Error updating proceeding',
        description: error.message || 'An unknown error occurred while updating the proceeding.',
      });
    }
  };

  const addHearingDate = () => {
    setHearingDates([...hearingDates, { startDate: '', endDate: '' }]);
    setIsFormDirty(true);
  };

  const removeHearingDate = (index: number) => {
    const updatedHearingDates = hearingDates.filter((_, i) => i !== index);
    setHearingDates(updatedHearingDates);
    setIsFormDirty(true);
  };

  const handleHearingDateChange = (
    index: number,
    field: 'startDate' | 'endDate',
    newDate: dayjs.Dayjs
  ) => {
    const updatedHearingDates = [...hearingDates];
    const formattedDate = newDate ? newDate.format('YYYY-MM-DD') : '';

    updatedHearingDates[index] = {
      ...updatedHearingDates[index],
      [field]: formattedDate,
    };

    const { startDate, endDate } = updatedHearingDates[index];

    if (startDate && endDate && dayjs(startDate).isAfter(dayjs(endDate))) {
      notification.error({
        message: 'Start date must be earlier than the end date.',
      });
      return;
    }

    setHearingDates(updatedHearingDates);
    setIsFormDirty(true);
  };

  const removeTechnicalMeetingDate = (index: number) => {
    const updatedTechnicalMeetingDates = technicalMeetingDates.filter((_, i) => i !== index);
    setTechnicalMeetingDates(updatedTechnicalMeetingDates);
    setIsFormDirty(true);
  };

  const removeSiteVisitDate = (index: number) => {
    const updatedSiteVisitDates = siteVisitDates.filter((_, i) => i !== index);
    setSiteVisitDates(updatedSiteVisitDates);
    setIsFormDirty(true);
  };

  const addMotionDate = () => {
    setMotionDates([...motionDates, { startDate: '', endDate: '' }]);
    setIsFormDirty(true);
  };

  const removeMotionDate = (index: number) => {
    const updatedDates = motionDates.filter((_, i) => i !== index);
    setMotionDates(updatedDates);
    setIsFormDirty(true);
  };

  const addTechnicalMeetingDate = () => {
    if (technicalMeetingDates.includes('')) {
      notification.error({ message: 'Please fill in the previous date first.' });
      return;
    }
    setTechnicalMeetingDates([...technicalMeetingDates, '']);
  };

  const addSiteVisitDate = () => {
    if (siteVisitDates.includes('')) {
      notification.error({ message: 'Please fill in the previous date first.' });
      return;
    }
    setSiteVisitDates([...siteVisitDates, '']);
  };

  const handleDateChange = (newDate: dayjs.Dayjs, index: number, type: string) => {
    const formattedDate = newDate ? newDate.format('YYYY-MM-DD') : '';

    let dates;
    if (type === 'technical') {
      dates = technicalMeetingDates;
    } else if (type === 'siteVisit') {
      dates = siteVisitDates;
    }

    if (dates.includes(formattedDate)) {
      notification.error({ message: 'This date is already added. Please select another date.' });
      return;
    }

    const updatedDates = [...dates];
    updatedDates[index] = formattedDate;

    if (type === 'technical') {
      setTechnicalMeetingDates(updatedDates);
    } else if (type === 'siteVisit') {
      setSiteVisitDates(updatedDates);
    }

    setIsFormDirty(true);
  };

  const handleMotionDateChange = (
    index: number,
    field: 'startDate' | 'endDate',
    newDate: dayjs.Dayjs
  ) => {
    const updatedMotionDates = [...motionDates];
    const formattedDate = newDate ? newDate.format('YYYY-MM-DD') : '';

    updatedMotionDates[index] = {
      ...updatedMotionDates[index],
      [field]: formattedDate,
    };

    const { startDate, endDate } = updatedMotionDates[index];

    if (startDate && endDate && dayjs(startDate).isAfter(dayjs(endDate))) {
      notification.error({
        message: 'Start date must be earlier than the end date.',
      });
      return;
    }

    const duplicate = updatedMotionDates.some((motion, idx) => {
      return idx !== index && motion.startDate === startDate && motion.endDate === endDate;
    });

    if (duplicate) {
      notification.error({
        message: 'This start and end date pair is already added. Please select different dates.',
      });
      return;
    }

    setMotionDates(updatedMotionDates);
    setIsFormDirty(true);
  };

  const setAsJoint = (checked: boolean) => {
    setIsJoint(checked ? 'yes' : 'no');
    setIsFormDirty(true);
  };

  useEffect(() => {
    if (updateError) {
      notification.error({
        message: 'Error updating proceedings',
        description: updateError.message || 'An unknown error occurred.',
      });
    }
  }, [updateError]);

  const handleCancelClick = () => {
    if (isFormDirty) {
      return (
        <Popconfirm
          title="Warning"
          description={
            <>
              {' '}
              Are you sure to cancel your changes? <br /> You will be redirected to the Proceeding
              List page.{' '}
            </>
          }
          onConfirm={() => navigate('/hearing')}
          okText="Yes"
          cancelText="No"
          placement="topRight"
        >
          <Button>Cancel</Button>
        </Popconfirm>
      );
    } else {
      return (
        <Link to="/hearing">
          <Button style={{ marginLeft: 8 }}>Cancel</Button>
        </Link>
      );
    }
  };

  const handleEnergyActivityChange = (value: string[]) => {
    setSelectedEnergyActivities(value);
    setShowOtherCommentBox(value.includes('Other'));
  };

  return (
    <div style={{ margin: '0 20px', minHeight: '100vh', marginTop: '10px' }}>
      <Row>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            width: '100%',
          }}
        >
          {loading || updating ? (
            <Skeleton />
          ) : (
            <Card
              title={<span className="highlighted-title">Proceeding Details</span>}
              style={{ width: '60%', overflowY: 'auto', margin: '0 auto' }}
              className="card"
            >
              <div style={{ border: '1px solid #d9d9d9', padding: '16px', borderRadius: '8px' }}>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onFinish}
                  onValuesChange={() => setIsFormDirty(true)}
                  style={{ width: '100%' }}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="description"
                        label="Description"
                        rules={[
                          {
                            required: true,
                            message: 'Please enter the description of the proceeding',
                            whitespace: true,
                          },
                        ]}
                      >
                        <TextArea
                          disabled={!isAuthenticated}
                          placeholder={isAuthenticated ? 'Please provide a value' : ''}
                          style={!isAuthenticated ? disabledInputStyle : {}}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="reportUpdate"
                        label="Proceeding Update"
                        rules={[
                          {
                            max: 1000,
                            message: 'Proceeding Update cannot exceed 1000 characters',
                          },
                        ]}
                      >
                        <TextArea
                          disabled={!isAuthenticated}
                          placeholder={isAuthenticated ? 'Please provide a value' : ''}
                          style={!isAuthenticated ? disabledInputStyle : {}}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {isAuthenticated && (
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item name="energyActivityTypes" label="Energy/Activity Types">
                          <Select
                            disabled={!isAuthenticated}
                            mode="multiple"
                            allowClear
                            placeholder={isAuthenticated ? 'Select energy/activity types' : ''}
                            options={energyActivityOptions}
                            onChange={handleEnergyActivityChange}
                            value={selectedEnergyActivities}
                            style={
                              !isAuthenticated
                                ? {
                                    backgroundColor: '#f5f5f5',
                                    color: '#595959',
                                    opacity: '1',
                                    border: '1px solid #d9d9d9',
                                  }
                                : {}
                            }
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  {showOtherCommentBox && isAuthenticated && (
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          name="otherComment"
                          label="Describe Other Energy/Activity Type:"
                          rules={[
                            {
                              required: true,
                              message: 'Please enter a comment for "Other"',
                            },
                          ]}
                        >
                          <CustomInput
                            showInput={true}
                            disabled={!isAuthenticated}
                            placeholder={
                              isAuthenticated ? 'Please enter a comment for "Other"' : ''
                            }
                            style={{
                              width: '100%',
                              backgroundColor: !isAuthenticated ? '#f5f5f5' : '',
                              color: !isAuthenticated ? '#595959' : '',
                              opacity: !isAuthenticated ? '1' : '',
                              border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  {isAuthenticated && (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="referralDate" label="Date File Referred for Hearing">
                          <DatePicker
                            disabled={!isAuthenticated}
                            disabledDate={(current) => current.isAfter(today)}
                            placeholder={isAuthenticated ? 'Select Date' : ''}
                            style={{
                              width: '100%',
                              backgroundColor: !isAuthenticated ? '#f5f5f5' : '',
                              color: !isAuthenticated ? '#595959' : '',
                              opacity: !isAuthenticated ? '1' : '',
                              border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="chcReceivedDate"
                          label="Chief Hearing Commissioner Received Panel Request Date"
                          data-testid="chc-received-date"
                        >
                          <DatePicker
                            disabled={!isAuthenticated}
                            disabledDate={(current) => current.isAfter(today)}
                            placeholder={isAuthenticated ? 'Select Date' : ''}
                            style={{
                              width: '100%',
                              backgroundColor: !isAuthenticated ? '#f5f5f5' : '',
                              color: !isAuthenticated ? '#595959' : '',
                              opacity: !isAuthenticated ? '1' : '',
                              border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="participantRequestDeadlineDate"
                        label="Participation Request Deadline Date"
                      >
                        <DatePicker
                          disabled={!isAuthenticated}
                          placeholder={isAuthenticated ? 'Select Date' : ''}
                          style={{
                            width: '100%',
                            backgroundColor: !isAuthenticated ? '#f5f5f5' : '',
                            color: !isAuthenticated ? '#595959' : '',
                            opacity: !isAuthenticated ? '1' : '',
                            border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                          }}
                        />
                      </Form.Item>
                    </Col>
                    {isAuthenticated && (
                      <Col span={12}>
                        <Form.Item name="hearingIssuesSetDate" label="Hearing Issues Are Set Date">
                          <DatePicker
                            disabled={!isAuthenticated}
                            placeholder={isAuthenticated ? 'Select Date' : ''}
                            style={{
                              width: '100%',
                              backgroundColor: !isAuthenticated ? '#f5f5f5' : '',
                              color: !isAuthenticated ? '#595959' : '',
                              opacity: !isAuthenticated ? '1' : '',
                              border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                            }}
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  {isAuthenticated && (
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          name="issuesComment"
                          label="Issues Comments"
                          rules={[
                            {
                              max: 5000,
                              message: 'Issues Comments cannot exceed 5000 characters',
                            },
                          ]}
                        >
                          <TextArea
                            disabled={!isAuthenticated}
                            placeholder={isAuthenticated ? 'Enter issues comments' : ''}
                            style={{
                              width: '100%',
                              backgroundColor: !isAuthenticated ? '#f5f5f5' : '',
                              color: !isAuthenticated ? '#595959' : '',
                              opacity: !isAuthenticated ? '1' : '',
                              border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="format" label="Format of Hearing">
                        <Select
                          disabled={!isAuthenticated}
                          options={formatOptions}
                          mode="multiple"
                          placeholder={isAuthenticated ? 'Select format(s)' : ''}
                          data-testid="format"
                          style={{
                            width: '100%',
                            backgroundColor: !isAuthenticated ? '#f5f5f5' : '',
                            color: !isAuthenticated ? '#595959' : '',
                            opacity: !isAuthenticated ? '1' : '',
                            border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="location" label="Hearing Location">
                        <CustomInput
                          showInput={true}
                          disabled={!isAuthenticated}
                          placeholder={isAuthenticated ? '' : ''}
                          style={{
                            width: '100%',
                            backgroundColor: !isAuthenticated ? '#f5f5f5' : '',
                            color: !isAuthenticated ? '#595959' : '',
                            opacity: !isAuthenticated ? '1' : '',
                            border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {isAuthenticated && (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item label="Pre-Hearing / Technical Meeting Dates">
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <div
                              style={{
                                border: '1px solid #d9d9d9',
                                borderRadius: '12px',
                                padding: '16px',
                                backgroundColor: !isAuthenticated ? '#f5f5f5' : '',
                              }}
                            >
                              {technicalMeetingDates.map((date, index) => (
                                <Row key={index} gutter={16} align="middle">
                                  <Col span={20}>
                                    <DatePicker
                                      disabled={!isAuthenticated}
                                      value={date ? dayjs(date) : null}
                                      onChange={(newDate) =>
                                        handleDateChange(newDate, index, 'technical')
                                      }
                                      data-testid={`technical-date-${index}`}
                                      placeholder={isAuthenticated ? '' : ''}
                                      style={{
                                        width: '100%',
                                        color: !isAuthenticated ? '#595959' : '',
                                        opacity: !isAuthenticated ? '1' : '',
                                        border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                                      }}
                                    />
                                  </Col>
                                  <Col span={4} style={{ textAlign: 'center' }}>
                                    <MinusCircleOutlined
                                      style={{
                                        fontSize: '20px',
                                        color: 'red',
                                        cursor: isAuthenticated ? 'pointer' : 'not-allowed',
                                      }}
                                      onClick={() => {
                                        if (isAuthenticated) {
                                          removeTechnicalMeetingDate(index);
                                        }
                                      }}
                                    />
                                  </Col>
                                </Row>
                              ))}
                              <Button
                                disabled={!isAuthenticated}
                                type="link"
                                data-testid="technical-meeting-add-dates"
                                icon={
                                  <PlusCircleOutlined
                                    style={{ fontSize: '20px', color: '#1890ff' }}
                                  />
                                }
                                onClick={addTechnicalMeetingDate}
                                style={{ marginTop: '8px' }}
                              >
                                Add Date
                              </Button>
                            </div>
                          </Space>
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item label="Site Visit Dates">
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <div
                              style={{
                                border: '1px solid #d9d9d9',
                                borderRadius: '12px',
                                padding: '16px',
                                backgroundColor: !isAuthenticated ? '#f5f5f5' : '',
                              }}
                            >
                              {siteVisitDates.map((date, index) => (
                                <Row key={index} gutter={16} align="middle">
                                  <Col span={20}>
                                    <DatePicker
                                      disabled={!isAuthenticated}
                                      value={date ? dayjs(date) : null}
                                      onChange={(newDate) =>
                                        handleDateChange(newDate, index, 'siteVisit')
                                      }
                                      placeholder={isAuthenticated ? '' : ''}
                                      data-testid={`sitevisit-date-${index}`}
                                      style={{
                                        width: '100%',
                                        color: !isAuthenticated ? '#595959' : '',
                                        opacity: !isAuthenticated ? '1' : '',
                                        border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                                      }}
                                    />
                                  </Col>
                                  <Col span={4} style={{ textAlign: 'center' }}>
                                    <MinusCircleOutlined
                                      style={{
                                        fontSize: '20px',
                                        color: 'red',
                                        cursor: isAuthenticated ? 'pointer' : 'not-allowed',
                                      }}
                                      onClick={() => {
                                        if (isAuthenticated) {
                                          removeSiteVisitDate(index);
                                        }
                                      }}
                                    />
                                  </Col>
                                </Row>
                              ))}
                              <Button
                                disabled={!isAuthenticated}
                                type="link"
                                data-testid="site-visits-add-dates"
                                icon={
                                  <PlusCircleOutlined
                                    style={{ fontSize: '20px', color: '#1890ff' }}
                                  />
                                }
                                onClick={addSiteVisitDate}
                                style={{ marginTop: '8px' }}
                              >
                                Add Date
                              </Button>
                            </div>
                          </Space>
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  {isAuthenticated && (
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item label="Hearing on a Motion Start and End Dates">
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <div
                              style={{
                                border: '1px solid #d9d9d9',
                                borderRadius: '12px',
                                padding: '16px',
                                backgroundColor: !isAuthenticated ? '#f5f5f5' : '',
                              }}
                            >
                              {motionDates.map((motion, index) => (
                                <Row key={index} gutter={16} align="middle">
                                  <Col span={10}>
                                    <DatePicker
                                      disabled={!isAuthenticated}
                                      value={motion.startDate ? dayjs(motion.startDate) : null}
                                      onChange={(newDate) =>
                                        handleMotionDateChange(index, 'startDate', newDate)
                                      }
                                      placeholder={isAuthenticated ? 'Start Date' : ''}
                                      data-testid={`motion-start-date-${index}`}
                                      style={{
                                        width: '100%',
                                        color: !isAuthenticated ? '#595959' : '',
                                        opacity: !isAuthenticated ? '1' : '',
                                        border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                                      }}
                                    />
                                  </Col>
                                  <Col span={10}>
                                    <DatePicker
                                      disabled={!isAuthenticated}
                                      value={motion.endDate ? dayjs(motion.endDate) : null}
                                      onChange={(newDate) =>
                                        handleMotionDateChange(index, 'endDate', newDate)
                                      }
                                      placeholder={isAuthenticated ? 'End Date' : ''}
                                      data-testid={`motion-end-date-${index}`}
                                      style={{
                                        width: '100%',
                                        color: !isAuthenticated ? '#595959' : '',
                                        opacity: !isAuthenticated ? '1' : '',
                                        border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                                      }}
                                    />
                                  </Col>
                                  <Col span={4} style={{ textAlign: 'center' }}>
                                    <MinusCircleOutlined
                                      style={{
                                        fontSize: '20px',
                                        color: 'red',
                                        cursor: isAuthenticated ? 'pointer' : 'not-allowed',
                                      }}
                                      data-testid={`motion-remove-date-${index}`}
                                      onClick={() => {
                                        if (isAuthenticated) {
                                          removeMotionDate(index);
                                        }
                                      }}
                                    />
                                  </Col>
                                </Row>
                              ))}
                              <Button
                                disabled={!isAuthenticated}
                                type="link"
                                data-testid="motion-add-dates"
                                icon={
                                  <PlusCircleOutlined
                                    style={{ fontSize: '20px', color: '#1890ff' }}
                                  />
                                }
                                onClick={addMotionDate}
                                style={{ marginTop: '8px' }}
                              >
                                Add Dates
                              </Button>
                            </div>
                          </Space>
                        </Form.Item>
                      </Col>
                    </Row>
                  )}

                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item label="Hearing Start and End Dates">
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <div
                            style={{
                              border: '1px solid #d9d9d9',
                              borderRadius: '12px',
                              padding: '16px',
                              backgroundColor: !isAuthenticated ? '#f5f5f5' : '',
                            }}
                          >
                            {hearingDates.map((hearing, index) => (
                              <Row key={index} gutter={16} align="middle">
                                <Col span={10}>
                                  <DatePicker
                                    disabled={!isAuthenticated}
                                    value={hearing.startDate ? dayjs(hearing.startDate) : null}
                                    onChange={(newDate) =>
                                      handleHearingDateChange(index, 'startDate', newDate)
                                    }
                                    placeholder={isAuthenticated ? 'Start Date' : ''}
                                    style={{
                                      width: '100%',
                                      color: !isAuthenticated ? '#595959' : '',
                                      opacity: !isAuthenticated ? '1' : '',
                                      border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                                    }}
                                    data-testid={`hearing-start-date-${index}`}
                                  />
                                </Col>
                                <Col span={10}>
                                  <DatePicker
                                    disabled={!isAuthenticated}
                                    value={hearing.endDate ? dayjs(hearing.endDate) : null}
                                    onChange={(newDate) =>
                                      handleHearingDateChange(index, 'endDate', newDate)
                                    }
                                    placeholder={isAuthenticated ? 'End Date' : ''}
                                    style={{
                                      width: '100%',
                                      color: !isAuthenticated ? '#595959' : '',
                                      opacity: !isAuthenticated ? '1' : '',
                                      border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                                    }}
                                    data-testid={`hearing-end-date-${index}`}
                                  />
                                </Col>
                                {isAuthenticated && (
                                  <Col span={4} style={{ textAlign: 'center' }}>
                                    <MinusCircleOutlined
                                      style={{
                                        fontSize: '20px',
                                        color: 'red',
                                        cursor: isAuthenticated ? 'pointer' : 'not-allowed',
                                      }}
                                      onClick={() => removeHearingDate(index)}
                                    />
                                  </Col>
                                )}
                              </Row>
                            ))}
                            {isAuthenticated && (
                              <Button
                                disabled={!isAuthenticated}
                                type="link"
                                icon={
                                  <PlusCircleOutlined
                                    style={{ fontSize: '20px', color: '#1890ff' }}
                                  />
                                }
                                onClick={addHearingDate}
                                style={{ marginTop: '8px' }}
                                data-testid="add-hearing-date"
                              >
                                Add Hearing Date
                              </Button>
                            )}
                          </div>
                        </Space>
                      </Form.Item>
                    </Col>
                  </Row>

                  {isAuthenticated && (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="informationSessionDate" label="Information Session Date">
                          <DatePicker
                            disabled={!isAuthenticated}
                            style={{
                              width: '100%',
                              backgroundColor: !isAuthenticated ? '#f5f5f5' : '',
                              color: !isAuthenticated ? '#595959' : '',
                              opacity: !isAuthenticated ? '1' : '',
                              border: !isAuthenticated ? '1px solid #d9d9d9' : '',
                            }}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item label="Is this a Joint Hearing">
                          <Switch
                            disabled={!isAuthenticated}
                            onChange={setAsJoint}
                            checked={isJoint === 'yes'}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>&nbsp;</Col>
                    </Row>
                  )}

                  {isAuthenticated && (
                    <Form.Item>
                      <Space>
                        <Button type="primary" htmlType="submit" disabled={!isFormDirty}>
                          Save Changes
                        </Button>
                        {handleCancelClick()}
                      </Space>
                    </Form.Item>
                  )}
                  {!isAuthenticated && (
                    <Link to="/hearing">
                      <Button type="primary" style={{ marginLeft: 8 }}>
                        Back
                      </Button>
                    </Link>
                  )}
                </Form>
              </div>
            </Card>
          )}
        </div>
      </Row>
    </div>
  );
};

export default ProceedingDetails;
